import axios, {
	type AxiosInstance,
	type AxiosRequestConfig,
	type AxiosResponse,
	type InternalAxiosRequestConfig,
	AxiosHeaders,
} from "axios";
import { parseRequestError } from "./utils";
import { apiErrorFingerprint, toApiRequestError } from "./errors";
import { EXCLUDED_API_PATHS } from "@/constants/sentry-constants";
import * as Sentry from "@sentry/react";

export const KASHU_GLOBAL_ON_ID = 28;
const API_BASE_URL = import.meta.env.VITE_API_BASE_URL;

/* ---------------- JSON client ---------------- */

export const axiosInstance = axios.create({
	baseURL: API_BASE_URL,
	timeout: 30000,
	headers: { "Content-Type": "application/json" },
});

export function setLocationIdForRequests(locationId: number) {
	axiosInstance.defaults.params = axiosInstance.defaults.params || {};
	axiosInstance.defaults.params.location_id = locationId;

	axiosMultipart.defaults.params = axiosMultipart.defaults.params || {};
	axiosMultipart.defaults.params.location_id = locationId;
}

function getDefaultRequestParams() {
	const { VITE_USER_ID } = import.meta.env;
	return {
		user_id: VITE_USER_ID,
		global_on_id: KASHU_GLOBAL_ON_ID,
	};
}

function appendDefaultsToFormData(fd: FormData): void {
	const defaults = getDefaultRequestParams();
	for (const [k, v] of Object.entries(defaults)) {
		if (v != null && !fd.has(k)) fd.append(k, String(v));
	}
}

axiosInstance.interceptors.request.use(
	(config: InternalAxiosRequestConfig) => {
		if (config.method === "get" || config.method === "delete") {
			config.params = {
				...(config.params ?? {}),
				...getDefaultRequestParams(),
			};
		} else if (isFormData(config.data)) {
			const h = config.headers;
			h?.delete?.("Content-Type");
			h?.delete?.("content-type");
			appendDefaultsToFormData(config.data);
		} else {
			const dataObj: Record<string, unknown> =
				(config.data as Record<string, unknown>) ?? {};
			config.data = { ...dataObj, ...getDefaultRequestParams() };
		}
		return config;
	},
	(error: unknown) => {
		const e = parseRequestError(error);
		return Promise.reject({
			code: e.code,
			message: e.message,
			details: e.details,
		});
	},
);

axiosInstance.interceptors.response.use(
	(response) => response,
	(error: unknown) => {
		const errorMessage = parseRequestError(error);
		const url = (error as { config?: { url?: string } })?.config?.url;
		const isExcluded = url
			? EXCLUDED_API_PATHS.some((p) => p.test(url))
			: false;

		const resp = (error as { response?: { status?: number } }).response;
		if (!isExcluded && resp) {
			const apiError = toApiRequestError(error, errorMessage);
			Sentry.captureException(apiError, {
				tags: {
					http_status: apiError.status,
					http_method: apiError.method,
					endpoint: apiError.url,
				},
				fingerprint: apiErrorFingerprint(apiError),
				extra: { details: errorMessage.details },
			});
		}

		return Promise.reject({
			code: errorMessage.code,
			message: errorMessage.message,
			details: errorMessage.details,
			http_status: resp?.status,
		});
	},
);

/* -------------- MULTIPART client (face uploads) -------------- */
/** Custom config for multipart requests that supports `public` */
export type MultipartAxiosConfig<D = unknown> = AxiosRequestConfig<D> & {
	public?: boolean;
};

/** Axios instance interface that knows about our custom `public` flag (no `any`) */
export interface AxiosMultipartInstance
	extends Omit<AxiosInstance, "post" | "request"> {
	post<T = unknown, R = AxiosResponse<T, unknown>, D = unknown>(
		url: string,
		data?: D,
		config?: MultipartAxiosConfig<D>,
	): Promise<R>;

	request<T = unknown, R = AxiosResponse<T, unknown>, D = unknown>(
		config: MultipartAxiosConfig<D> & { url: string; method?: string },
	): Promise<R>;
}

export const axiosMultipart: AxiosMultipartInstance = axios.create({
	baseURL: API_BASE_URL,
	timeout: 30000,
	// intentionally do not set Content-Type; browser must set multipart boundary
}) as unknown as AxiosMultipartInstance;

function isFormData(x: unknown): x is FormData {
	return typeof FormData !== "undefined" && x instanceof FormData;
}

axiosMultipart.interceptors.request.use(
	(config: InternalAxiosRequestConfig & { public?: boolean }) => {
		// Public endpoints must not send bearer tokens
		if (config.public) {
			const h = config.headers as AxiosHeaders;
			h?.delete?.("Authorization");
			h?.delete?.("authorization");
		}

		// Never force Content-Type for multipart — let the browser set the boundary
		{
			const h = config.headers;
			h?.delete?.("Content-Type");
			h?.delete?.("content-type");
		}

		// Ensure we have FormData - but don't try to convert arbitrary objects
		if (!isFormData(config.data)) {
			throw new Error(
				"axiosMultipart only accepts FormData. Use axiosInstance for JSON requests.",
			);
		}
		const fd = config.data;

		// Append defaults if not already present (user_id, location_id, global_on_id)
		const defaults = getDefaultRequestParams();
		for (const [k, v] of Object.entries(defaults)) {
			if (v != null && !fd.has(k)) fd.append(k, String(v));
		}

		config.data = fd;
		return config;
	},
	(error: unknown) => {
		const e = parseRequestError(error);
		return Promise.reject({
			code: e.code,
			message: e.message,
			details: e.details,
		});
	},
);

axiosMultipart.interceptors.response.use(
	(response) => response,
	(error: unknown) => {
		const errorMessage = parseRequestError(error);
		const url = (error as { config?: { url?: string } })?.config?.url;
		const isExcluded = url
			? EXCLUDED_API_PATHS.some((p) => p.test(url))
			: false;

		const resp = (error as { response?: { status?: number } }).response;
		if (!isExcluded && resp) {
			const apiError = toApiRequestError(error, errorMessage);
			Sentry.captureException(apiError, {
				tags: {
					http_status: apiError.status,
					http_method: apiError.method,
					endpoint: apiError.url,
				},
				fingerprint: apiErrorFingerprint(apiError),
				extra: { details: errorMessage.details },
			});
		}

		return Promise.reject({
			code: errorMessage.code,
			message: errorMessage.message,
			details: errorMessage.details,
			http_status:
				(error as { response?: { status?: number } }).response?.status ?? 0,
		});
	},
);
